/**
 * Preisspiegel (#2388) — a stock-market-styled price browser over the German
 * farm-product catalog. House pattern (mirrors Ernteprognose / Liquiditäts-
 * planung): a design-system ``LensePanel`` (Warengruppe → Kategorie, with a
 * disable cascade) on the left; a workspace on the right that shows the L3 tile
 * grid for the selected Kategorie, or the price chart for a drilled-in tile.
 *
 * Empty-by-default (#2406): the page renders the REAL tenant own-price layer
 * (#2394 ``price_observation`` facts) and nothing else — a tenant without data
 * sees honest "keine Daten" states. The route itself stays gated by
 * ``apps.preisspiegel`` (the pilot app-gate flag).
 *
 * URL state: ``?kat=`` (Kategorie) · ``?item=`` (drilled item) · ``?g=``
 * aggregation grain (HOF-6 — ``day``/``week``/``month``) · ``?q=`` quality ·
 * ``?view=`` (alle | kanal) · ``?py=`` prior-year overlays (HOF-6 — ``1,2,3``)
 * · ``?d=`` (#2508 — a deep-linked observation day, pre-selecting that bucket's
 * detail panel; dropped whenever the item or Kategorie changes, since a day is
 * item-specific).
 *
 * HOF-6 retired three of them. ``?p=`` (the ZEITRAUM preset) has no meaning
 * against a fixed one-year axis; ``?side=`` has none now that both sides are
 * drawn at once; ``?pm=`` had only two values and one of them was the candle
 * mode. All three are accept-and-IGNORE rather than errors — an old shared link
 * still opens the item it names, just on the new chart.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { LensePanel, PageLoadOverlay, useMobileSidePanel } from '@hofsync/design-system';
import {
  CATALOG,
  findItem,
  findKategorie,
  mergeDerivedItems,
  resolveKategorieScope,
  servedTilesFromWire,
  type Quality,
} from './catalog';
import {
  buildCatalogState,
  buildLensItems,
  findKategorieState,
  hasAnyData,
  resolveSelectedKategorie,
} from './disableCascade';
import { availableQualities } from './axes';
import { DEFAULT_GRAIN, resolveGrain, type Grain } from './grain';
import { priorYearsParam, resolvePriorYears } from './priorYears';
import { type ChartView } from './aggregateChartModel';
import { usePreisspiegelTiles } from '../../../hooks/usePreisspiegelTiles';
import { useTenantScopeId } from '../../../hooks/useTenantScopeId';
import { readStoredKategorie, writeStoredKategorie } from './katPreference';
import PriceTileGrid, { type PriceAvailability } from './PriceTileGrid';
import PriceChart from './PriceChart';
import { todayIso } from '../../../utils/today';
import './PreisspiegelPage.css';

/**
 * The id itself when the catalog knows it — as a Kategorie, or (HOF-299 S3) as a
 * Warengruppe, which is what the retired Kategorie ids `saatgut` / `duengemittel` /
 * `pflanzenschutz` / `maschinen_arbeit` / `pflanzgut` are now. A Warengruppe id is
 * forwarded as-is: `/tiles` resolves it to the Warengruppe's first data-bearing
 * Kategorie by the same rule `resolveSelectedKategorie` applies here. Else null.
 */
function knownScope(id: string | null): string | null {
  return resolveKategorieScope(id) ? id : null;
}

export default function PreisspiegelPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const mobilePanel = useMobileSidePanel();
  const today = todayIso();

  // ── ONE read, and why it used to be two (#2694 → HOF-271) ─────────────────
  //
  // The SHELL — the lens, the #2420 hide-empty-subtree rule, which tiles exist —
  // needs only "does this item have data", which the index answers in one row
  // per (item, side): a few dozen rows bounded by the CATALOG, not by how much
  // the farm sold. The PRICES are a separate, far larger read, and #2694 split
  // them so a farmer stopped waiting on a whole-tenant download just to find out
  // which of a locally-known list to draw.
  //
  // What #2694 could not remove is that the two then ran in SERIES: the price
  // read was scoped to the items the index had just reported as populated, so
  // the browser paid index latency and only then cell latency — ~5,5 s to tiles
  // at the prod median on WF, ~12 s at p90.
  //
  // HOF-271 removes the dependency at its root rather than the second request.
  // The grid never needed "which items have data"; it needed "which items are in
  // the open Kategorie", and that is a CATALOG fact the backend can read for
  // itself (`config/catalog/preisspiegel@v1.yaml`, #2771). So both halves come
  // back from one `GET /twin/preisspiegel/tiles`, gathered server-side, and the
  // shape of each half is unchanged — `usePreisspiegelTiles` derives them with
  // the same builders the two standalone hooks use.
  //
  // The scope below is still WHAT IS ON SCREEN, for the reason #2694 gave: the
  // grid pays for its Kategorie, the drilled-in chart for its one item. What
  // changed is only that resolving the scope no longer needs an answer from the
  // server first.
  const katParam = searchParams.get('kat');
  const itemParam = searchParams.get('item');

  // HOF-101 — which tenant's remembered lens applies. Withheld (`resolved`
  // false) while auth is in flight; see the stamping effect below for why that
  // distinction has to reach this page rather than being flattened to null.
  const { tenantId, resolved: tenantResolved } = useTenantScopeId();

  // HOF-101 — the remembered Kategorie, read once per tenant. Deliberately NOT
  // in a lazy `useState` initializer (the house no-flash idiom, `useTileViewMode`):
  // that pattern fits a preference whose namespace is known at mount, and this
  // one's is not — the tenant arrives with `/auth/me`. Re-reading when the scope
  // changes is what makes „Anzeige als" hand over the right farm's lens.
  const storedKatId = useMemo(
    () => (tenantResolved ? readStoredKategorie(tenantId) : null),
    [tenantResolved, tenantId],
  );

  // A deep link into ONE item carries its Kategorie implicitly: the catalog is
  // the only authority on which Kategorie an item lives under, and the #2538
  // Deckungsbeitrag drawer already derives its `?kat=` that way. Reading it here
  // is what keeps a `?item=` link at one request — otherwise the page would ask
  // for a Kategorie, then discover the drilled item and ask again.
  const linkedItem = useMemo(() => (itemParam ? findItem(itemParam) : null), [itemParam]);

  // What we ASK the server to scope the cells to. Every input is known before
  // any response: a URL param, the catalog, or a `localStorage` read. `null` is
  // a real request — the cold visit, where the server applies the same
  // first-enabled rule (#2420/HOF-101) to the index it is already computing.
  //
  // Deliberately NOT derived from `state` below. That would be circular (the
  // scope would depend on the read it scopes) and would cost the round trip this
  // issue exists to remove. The server falls through an empty Kategorie for us,
  // by the same rule and off the same rows, so the two cannot disagree.
  // The linked item outranks an explicit `?kat=`, and that order is load-bearing
  // rather than a taste (codex r2 P1). Both travel in one scope — `items` is what
  // to READ, `kat` only where to fall BACK to — so when a link carries both and
  // they disagree, the server answers with the ITEM's cells. Read `?kat=` first
  // and the page then resolves the other Kategorie and draws its tiles over
  // those cells: every one „Keine Daten", about items the index in the same
  // response reports data for, and the chart the URL asked for never rendered.
  // The item is the more specific statement AND the one the fetch honours, so it
  // is the one the render must follow. A bare `?kat=` is untouched by this — it
  // still beats the stored preference and first-enabled (HOF-101).
  //
  // Only an id the CATALOG knows is ever asked for. The server answers a `kat`
  // it cannot resolve with 422 rather than falling through (a typo must not be
  // served the catalog-wide read). Until Pflanzgut became a Warengruppe (2026-09)
  // `pflanzgut` WAS a Kategorie id — and a day later (HOF-299 S3) `saatgut`,
  // `duengemittel`, `pflanzenschutz` and `maschinen_arbeit` made the same move;
  // all five sit in bookmarks, in the e2e specs and in every
  // `hofsync.preisspiegel.kat` preference of a reader who last opened one. A
  // WARENGRUPPE id is therefore forwarded, and `/tiles` resolves it to the
  // Warengruppe's first data-bearing Kategorie by the same rule
  // `resolveSelectedKategorie` applies below — so the old links keep opening the
  // lens they named. An id that is neither is dropped here and lets the next
  // candidate in the precedence stand, exactly as a stored Kategorie that has
  // left the catalog always has (HOF-101). A legacy link that also names an
  // `?item=` needs none of this: the item's own Kategorie already outranks
  // `?kat=` above.
  //
  // Deliberately EVERY unknown id, not only the retired `pflanzgut` (codex round 1,
  // P2, considered and declined). Before HOF-271 an unknown `?kat=` never reached
  // the server at all — `resolveSelectedKategorie` fell through to first-enabled
  // and the page said so in its docstring — so this restores the page's own rule
  // rather than inventing one, and it is the same rule HOF-101 states for a stored
  // id that has left the catalog. The 422 is the API's contract with a caller that
  // asks for a Kategorie by name; the page asking for nothing is not a bypass of
  // it, and a typo'd link opening the reader's own lens beats an error page.
  const requestedKat = linkedItem?.kategorie.id || knownScope(katParam) || knownScope(storedKatId) || null;
  //
  // A drilled-in scope carries BOTH: `items` is what to read, and `kat` is where
  // to fall back to. It is not redundant — `selectedItem` requires
  // `itemHasData`, so a `?item=` the tenant has no observations for is never
  // drilled into and the page renders that Kategorie's GRID instead. Since the
  // request no longer waits for the index, only the server can apply that rule,
  // and it needs the Kategorie to apply it TO. Send the item alone and the
  // answer is one dead item's empty cells under a grid of live tiles, every one
  // of them reading "Keine Daten" over data the same response's index reports.
  //
  // HOF-299 S3 — an `?item=` the STATIC catalog does not know may be a DERIVED
  // tile: one of the tenant's own `product` entities, which only the response can
  // name. The request cannot wait for it (that is the round trip HOF-271
  // removed), so such a link is asked for OPTIMISTICALLY as an item scope under
  // the Kategorie the link carries. The server honours an explicit item it holds
  // data for and falls back to `kat` otherwise — the same rule as for a dead
  // static item — so a typo'd `?item=` still opens the Kategorie's grid, and a
  // real product link is one request, not two. A derived link therefore carries
  // its `?kat=` (the drawer derives it through `findItem` over the served roster);
  // one without it is a cold visit.
  const scope = useMemo(() => {
    if (linkedItem) return { items: [linkedItem.item.id], kat: requestedKat };
    if (itemParam && requestedKat) return { items: [itemParam], kat: requestedKat };
    return { kat: requestedKat };
  }, [linkedItem, itemParam, requestedKat]);

  // HOF-270 — `summaryFor` is the grid's price source now; `priceRead` is the
  // chart's, and the AC 17 fallback's. The hook also reports `fallbackItems`, and
  // this page deliberately does NOT render it: a fallen-back tile is CORRECT, it
  // was merely computed the slow way, so telling the farmer would be noise about
  // an operator's problem. The count is the server's log event, where the
  // operator is (`preisspiegel_tile_fallback`).
  const {
    index: indexRead,
    cells: priceRead,
    summaryFor,
    // HOF-377 — the MATERIALISED benchmark presence bit and whether the curves have
    // landed. Both come off the same read the tiles do, which is what lets the grid
    // stop gathering the benchmarks for a boolean.
    benchmarkLevelsFor,
    sparklines,
    derivedTiles: derivedWire,
  } = usePreisspiegelTiles(scope);

  // HOF-299 S3 — the tenant's EFFECTIVE catalog: the compiled static structure
  // with the served product tiles merged into their derived Kategorien. Everything
  // below that reads the tree — the lens cascade, the open Kategorie's grid, the
  // drilled item — reads THIS, so a product tile is a tile like any other. Tiles
  // for exact products are never in the static catalog; they only ever arrive here.
  const derivedTiles = useMemo(() => servedTilesFromWire(derivedWire), [derivedWire]);
  const catalog = useMemo(() => mergeDerivedItems(CATALOG, derivedTiles), [derivedTiles]);
  const {
    itemHasData,
    // #2944 — which unit each item's facts were PUBLISHED under. The index is
    // the only read that carries it (the catalog-wide `/cells` group key is at
    // UDB's ceiling), so the page is where it meets the series it labels.
    unitsByItem,
    // The book's own units, which `unitsByItem` hides behind the trades'. Only
    // the chart's reference LEVEL needs them — it is the one surface rendering
    // the book's own number.
    estimateUnitsByItem,
    isLoading: indexLoading,
    dataQuality: indexQuality,
    unavailable: indexUnavailable,
  } = indexRead;

  // Enable/disable cascade over the own-price layer (recomputes when facts arrive).
  const state = useMemo(() => buildCatalogState(itemHasData, catalog), [itemHasData, catalog]);
  // #2420: the lens renders ONLY the data-bearing subtree (hide, don't grey);
  // an all-empty tenant collapses to no rows and the honest empty state below.
  const lensItems = useMemo(() => buildLensItems(state), [state]);
  const anyData = useMemo(() => hasAnyData(state), [state]);

  // HOF-101 — precedence: an explicit ?kat= (a statement about THIS visit) beats
  // the stored preference, which beats first-enabled. The stored id goes through
  // `resolveSelectedKategorie` like any other candidate, so a Kategorie that
  // carries no data for this tenant falls through to first-enabled instead of
  // opening an empty grid (#2420 — the lens shows only the data-bearing subtree).
  //
  // Resolved from `requestedKat` — the SAME expression the request above was
  // built from, deliberately, and not a second copy of its precedence.
  //
  // While the grid took two reads this could not go wrong: the price read was
  // scoped to what the index had just answered, so „what was fetched" was
  // derived from „what is on screen". HOF-271 inverts that — the scope is
  // resolved from the URL and the catalog *before* any response exists, which is
  // what removes the round trip — so these are now two computations that must
  // agree, and the only way to guarantee they do is to give them one input.
  //
  // Spelling the precedence out twice is exactly how they came apart (codex r1
  // P1): this line read `katParam || storedKatId` and so ignored `?item=`
  // entirely, while the request derived that item's Kategorie and fetched it. An
  // `?item=erdbeeren` link fetched Beerenobst's cells and rendered *Getreide* —
  // first-enabled — drawing its tiles as „Keine Daten" over items the index in
  // the same response reported data for, and never rendering the chart the URL
  // asked for at all.
  //
  // The `||` chain in `requestedKat` also keeps the `''` reading this comment
  // has always described: a bare `?kat=` reads back as empty and the stamping
  // effect below treats it as ABSENT (`if (katParam || …)`), so two notions of
  // absent across the two would make an empty param the one URL that silently
  // suppresses the preference and then stamps first-enabled over it.
  const selectedKatId = useMemo(() => resolveSelectedKategorie(requestedKat, state), [requestedKat, state]);
  const katLoc = useMemo(
    () => (selectedKatId ? findKategorieState(state, selectedKatId) : null),
    [selectedKatId, state],
  );
  const catalogKat = useMemo(
    () => (selectedKatId ? findKategorie(selectedKatId, catalog) : null),
    [selectedKatId, catalog],
  );

  // #2420: the workspace shows only the populated items of the selected
  // Kategorie — a no-data item is hidden, not rendered as a "Keine Daten" tile.
  const visibleItems = useMemo(
    () => catalogKat?.items.filter((it) => itemHasData(it.id)) ?? [],
    [catalogKat, itemHasData],
  );

  const selectedItem = useMemo(() => {
    if (!itemParam || !catalogKat) return null;
    const it = catalogKat.items.find((i) => i.id === itemParam);
    return it && itemHasData(it.id) ? it : null;
  }, [itemParam, catalogKat, itemHasData]);

  // #2397: the REAL tenant own-price layer (#2394 price_observation facts), read
  // via the twin/cells surface — since HOF-271 as the second half of the ONE
  // `/tiles` response the shell above came from. On load/error the maps are
  // empty and the page renders honest "keine Daten" states (#2406).
  //
  // There is ONE read where there used to be two callers, because there was only
  // ever one LIVE: the grid's set collapsed to `[]` while the chart was up and
  // the chart's did while the grid was. Its scope is the same one #2694 chose —
  //
  //  - the GRID pays for the visible Kategorie. A ?kat=saatgut deep link
  //    provably reads no berry cell.
  //  - the CHART pays for exactly the drilled item, because it draws that item
  //    and nothing else. A ?item= deep link used to fetch every sibling item of
  //    the surrounding Kategorie to draw one line.
  //
  // — resolved from the URL and the catalog rather than from a first response
  // (see `scope` above). The cache key is still deliberately NOT subset-aware
  // (`preisspiegelTilesKeys.scope`): a drilled-in chart asking for one item while
  // its Kategorie's entry is warm WILL go to the network, because one entry
  // behind three scopes would leave nothing on screen able to say which scope a
  // `data_quality` claim was about. That is a real, accepted cost — a drill-in
  // from the grid spends a round trip behind "Preisverlauf wird geladen …" — and
  // if it stops being worth it the fix is to PREFETCH the item's scope on tile
  // hover, never to widen the key.
  //
  // The INDEX half is carried across that key change (see
  // `usePreisspiegelTiles`), so the lens does not blank while the chart loads.
  // The cells half is not, and must not be: the previous scope's cells under
  // these tiles would render "Keine Daten" with the confidence of a read that
  // had covered them.
  const {
    seriesByItem: ownByItem,
    benchmarkSeriesByItem: benchmarkByItem,
    isLoading: pricesLoading,
    unavailable: pricesUnavailable,
    dataQuality: priceQuality,
  } = priceRead;

  // What the price layer can say about an item the INDEX vouches for. A failed
  // price read is NOT "ready": the tiles are still on screen (they come from the
  // index), so leaving it at ready would render "Keine Daten" across a grid the
  // page already knows holds observations — the exact contradiction the skeleton
  // exists to prevent, just reached through the error path instead of the slow
  // path.
  //
  // `unavailable`, not `error`: a failed REFETCH over rows we still hold is not
  // a failure to answer. Reporting it as one would put "Preis nicht verfügbar"
  // on the empty tiles of an otherwise complete grid, or take a correct chart
  // off screen.
  // ONE availability, where there used to be a ternary over two callers
  // (HOF-271). The page always said "the read the reader is looking at", and
  // picking it out of two hooks was how that was expressed while the grid and
  // the chart had separate ones. They now share a read whose SCOPE is the thing
  // the reader is looking at, so the choice is made where the request is made
  // and there is nothing left to choose between here.
  //
  // That removes the trap the old ternary existed to avoid rather than
  // relocating it: there is no longer a suspended caller whose EMPTY_QUALITY
  // could be read as a completeness claim about the other's rows — no read
  // qualifies rows it did not fetch, because there is only one read.
  const prices: PriceAvailability = pricesLoading ? 'loading' : pricesUnavailable ? 'unavailable' : 'ready';

  // Each read qualifies only its OWN numbers. The price clause additionally
  // waits for that read to settle: while it is in flight its observation count
  // is 0, and "(0 gelesen)" beside a grid of skeletons states a shortfall
  // nothing has measured yet. A price ERROR is spoken by the error banner
  // instead — but it must not silence the INDEX's clause, which is about a
  // different read and a different (worse) failure.
  // #2944 — `degraded` is raised by THREE different things and only one of them
  // is a read that fell short: a truncated/failed read, `dateless_groups`, and
  // `mixed_unit_items`. The latter two are COMPLETE reads whose rows are merely
  // unusable in one specific way, and the page says something quite different
  // about them ("these positions cannot be labelled") than about the first
  // ("whole Kategorien may be missing from this page").
  //
  // `readDegraded` is the server's own statement of which it was. It is NOT
  // derived here by subtracting the counters from `degraded` — that inference is
  // unsound in both directions, since one boolean can carry several causes at
  // once: a genuinely degraded read that ALSO has a dateless group would have
  // its shortfall explained away and silently under-warned.
  const indexShortfall = indexQuality.truncated || indexQuality.malformed > 0 || indexQuality.readDegraded;
  const indexIncomplete =
    indexShortfall ||
    indexQuality.datelessGroups > 0 ||
    indexQuality.mixedUnitItems > 0 ||
    // HOF-19 (codex P1): a failed benchmark presence read hides benchmark-only
    // tiles — items ARE missing from the page, which is exactly the claim this
    // banner exists to make. ONE pair, because `/index` folds every benchmark
    // leg into one presence row and its flags say only that the merged answer
    // is incomplete. Not inert for most tenants: the shared reference zone is
    // served to all of them.
    indexQuality.benchmarkDegraded ||
    indexQuality.benchmarkTruncated;
  const pricesIncomplete = prices === 'ready' && (priceQuality.truncated || priceQuality.degraded);

  const getSeriesForItem = useCallback((itemId: string) => ownByItem[itemId] ?? [], [ownByItem]);
  // HOF-19 / AC 22 (codex r2): the fact a tile's item carries benchmark data,
  // so a benchmark-only tile renders openable rather than "Keine Daten",
  // disabled — visible but dead. HOF-248: the SERIES IDS, because the tile also
  // has to name which population it holds, and a boolean cannot.
  const itemBenchmarkSeriesIds = useCallback(
    (itemId: string) => (benchmarkByItem[itemId] ?? []).map((s) => s.seriesId),
    [benchmarkByItem],
  );

  const seriesForItem = useMemo(
    () => (selectedItem ? (ownByItem[selectedItem.id] ?? []) : []),
    [selectedItem, ownByItem],
  );

  // HOF-19 Phase B — the AMI benchmark for the drilled item, from its own map.
  // Separate from `seriesForItem` all the way down: the chart attaches it after
  // the own-price model is built, so no own statistic can move (AC 8).
  const benchmarkForItem = useMemo(
    () => (selectedItem ? (benchmarkByItem[selectedItem.id] ?? []) : []),
    [selectedItem, benchmarkByItem],
  );

  // ── Chart control resolution (URL → validated selection) ──────────────────
  //
  // HOF-6 removed the side resolution entirely: the chart draws Kauf and Verkauf
  // together, so there is no one side for the page to pick, and the Qualität
  // axis is offered across both (a Qualität backed only by the purchase series
  // would otherwise be unreachable, with no switcher left to reach it with).
  const qualityList = useMemo(() => availableQualities(seriesForItem), [seriesForItem]);
  const quality = useMemo<Quality | undefined>(() => {
    if (!selectedItem?.quality) return undefined;
    const p = searchParams.get('q') as Quality | null;
    if (p && qualityList.includes(p)) return p;
    return qualityList[0];
  }, [searchParams, selectedItem, qualityList]);

  // HOF-6 — how much time one point stands for. Replaces `?p=`, which chose how
  // much time the whole AXIS showed; the axis is a fixed year now.
  const grain = useMemo<Grain>(() => resolveGrain(searchParams.get('g')), [searchParams]);
  const view = useMemo<ChartView>(() => (searchParams.get('view') === 'kanal' ? 'kanal' : 'alle'), [searchParams]);
  // HOF-6 — the prior-year overlays, the replacement for panning back through
  // history: `?py=1,2,3`, same spelling as ernteprognose's own.
  const priorYears = useMemo(() => resolvePriorYears(searchParams.get('py')), [searchParams]);
  // #2508 — the deep-linked observation day. Validated by the chart against the item's own
  // observations (an unobserved day falls through to the latest), so no check is needed here.
  const day = searchParams.get('d');

  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Persist a default ?kat= once (deep-linkable). A hidden/unknown ?kat= is left
  // in the URL untouched: resolveSelectedKategorie already RENDERS first-enabled
  // / the empty state (#2420, no ghost grid), so overwriting the param here when
  // the data hasn't yet loaded would drop an otherwise-valid deep link.
  //
  // HOF-101 — and equally, not before the TENANT is known. The preference cannot
  // be read until `/auth/me` lands, and stamping first-enabled in the meantime
  // would write a parameter this page then treats as an explicit deep link: the
  // arriving preference would lose to the page's own guess, on every cold visit.
  // `ProtectedLayout` already holds the route until auth settles, so in the app
  // this gate rarely fires — it is what keeps the precedence a property of the
  // page rather than of who happens to mount it.
  useEffect(() => {
    if (katParam || !selectedKatId || !tenantResolved) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('kat', selectedKatId);
        return next;
      },
      { replace: true },
    );
  }, [katParam, selectedKatId, tenantResolved, setSearchParams]);

  // #2508 — ``d`` is an item-specific observation day, so it is dropped alongside the quality
  // axis whenever the drilled item (or the whole Kategorie) changes. `?g=`/`?py=`/`?view=` are
  // deliberately NOT dropped: they are how the reader wants to LOOK at a price series, not
  // facts about one item, so carrying them across a switch is what they mean by setting them.
  //
  // HOF-101 — and this, an EXPLICIT selection, is the only thing that writes the
  // preference. Resolution deliberately does not: merely viewing a `?kat=` deep
  // link someone shared is not a choice, and recording it would let that link
  // silently redefine the reader's own default.
  const handleSelectKat = useCallback(
    (katId: string) => {
      writeStoredKategorie(tenantId, katId);
      setParams({ kat: katId, item: null, q: null, d: null });
    },
    [setParams, tenantId],
  );

  const handleLensSelect = useCallback(
    (itemId: string, subItemId: string | null) => {
      if (subItemId) {
        handleSelectKat(subItemId);
        return;
      }
      const wg = state.find((w) => w.id === itemId);
      const firstKat = wg?.kategorien.find((k) => k.enabled) ?? wg?.kategorien[0];
      if (firstKat) handleSelectKat(firstKat.id);
    },
    [state, handleSelectKat],
  );

  const handleSelectItem = useCallback((id: string) => setParams({ item: id, q: null, d: null }), [setParams]);
  const handleBackToGrid = useCallback(() => setParams({ item: null }), [setParams]);

  // HOF-6 — a prior-year chip toggles ONE offset and leaves the rest alone. The
  // whole set is re-serialised (rather than the param being appended to) so the
  // URL stays canonical: ascending, de-duplicated, and absent when nothing is on.
  const handleTogglePriorYear = useCallback(
    (offset: number) => {
      const next = priorYears.includes(offset) ? priorYears.filter((o) => o !== offset) : [...priorYears, offset];
      setParams({ py: priorYearsParam(next) });
    },
    [priorYears, setParams],
  );

  const workspaceTitle = selectedItem?.name ?? catalogKat?.name ?? 'Preisspiegel';

  return (
    <div className="preisspiegel-page">
      <LensePanel
        title="Preisspiegel"
        items={lensItems}
        selectedItem={katLoc?.warengruppe.id ?? undefined}
        selectedSubItem={selectedKatId ?? undefined}
        onSelect={handleLensSelect}
        isMobileOpen={mobilePanel?.isOpen ?? false}
        onMobileClose={() => mobilePanel?.close()}
      />
      {/* #2694 — gated on the INDEX query only. The prices arrive behind the
          shell and are rendered as per-tile skeletons, so the page is
          interactive as soon as we know what the catalog contains. */}
      <PageLoadOverlay visible={indexLoading} elapsedSeconds={0} />

      <div className="preisspiegel-page__main">
        <header className="preisspiegel-page__header">
          <div className="preisspiegel-page__heading">
            <h1 className="preisspiegel-page__title">{workspaceTitle}</h1>
          </div>
          {selectedItem && (
            <button type="button" className="preisspiegel-page__back" onClick={handleBackToGrid}>
              ← {catalogKat?.name ?? 'Übersicht'}
            </button>
          )}
        </header>

        {/* #2694 — gated on the ACTIVE read's `unavailable`, not on the raw
            error. A failed REFETCH over rows we still hold is not a failure to
            answer: React Query keeps the last good data, so announcing it here
            would put "Eigene Preise konnten nicht geladen werden." directly
            above a complete, correct grid or chart. The banner speaks only when
            we have nothing for what is on screen. */}
        {/* HOF-271: `&& !indexUnavailable` — the narrower sentence, only when the
            broader one is not being said. Since the shell and the prices share
            one request, a failure that leaves us holding nothing takes BOTH, and
            two alerts about one failure is noise that makes the page look more
            broken than it is. The `indexUnavailable` banner below is the
            stronger claim and subsumes this one; this still speaks alone in the
            case it was written for, which the merge did not remove: a degraded
            cell half arriving beside a readable index. */}
        {pricesUnavailable && !indexUnavailable && (
          <div className="preisspiegel-page__error" role="status">
            Eigene Preise konnten nicht geladen werden.
          </div>
        )}

        {/* #2694 — the index read produced no usable answer: it threw, or it
            came back 200-with-an-empty-index because the server could not read
            the population (its declared failure posture is never a 500).
            role="alert", and deliberately NOT the "Noch keine Preisdaten" empty
            state below.

            The distinction is the whole point. Both look identical on the wire —
            zero items — but they are opposite claims: one says "you have no
            price history", the other says "we could not find out". Collapsing
            them would tell a farmer holding a full season of sales that they
            have none, at the exact moment the backend is having a bad minute,
            and nothing on the page would contradict it. */}
        {indexUnavailable && (
          <div className="preisspiegel-page__error" role="alert">
            <strong>Übersicht konnte nicht geladen werden.</strong> Ob und welche Preisdaten vorliegen, lässt sich
            gerade nicht feststellen — das heißt <em>nicht</em>, dass keine erfasst sind. Bitte laden Sie die Seite neu
            oder wenden Sie sich an den Support.
          </div>
        )}

        {/* #2573: the read came back incomplete, so every series below is
            missing observations. role="alert" (not "status") — this qualifies
            numbers the farmer may act on, and it names WHICH numbers are
            suspect rather than being generic. The page keeps rendering
            underneath: the prices are incomplete, not absent, and hiding them
            would be a worse answer than showing them with a warning.

            #2646 moved the SOURCE of this from the FE's own page-walk cap to
            the server's `data_quality`, which is the only one that can see the
            whole population. `degraded` joins `truncated`: "we could not read
            part of your data" undermines the same numbers as "we stopped
            early", so it must not be the quieter of the two.

            It is also SCOPED now. It used to be a statement about the
            whole-namespace read, rendered identically on every Kategorie —
            including ones with no Verlauf for it to be qualifying. It now
            speaks about the category in view, and names the ambiguity #2420
            creates on an incomplete read: the lens shows only the data-bearing
            subtree, so a Kategorie whose observations did not arrive is not
            rendered empty, it is not rendered at all. Nothing else on the page
            can tell the farmer that "Saatgut is missing" and "you never sold
            Saatgut" look identical here. */}
        {/* #2694 — the banner now speaks for BOTH reads, and each clause says
            only what its own read can actually know. That split is a real
            consequence of the index, not tidiness:

            "Auch ganze Kategorien können in der Übersicht fehlen" used to be
            attached to the PRICE read, and it was true then — the lens was built
            from the price cells, so a truncated read hid whole Kategorien. It is
            now FALSE of the price read and TRUE of the index. The catalog comes
            from the index, so a truncated price read leaves every Kategorie
            listed and merely empties tiles inside it (visible, not absent),
            while a truncated INDEX is the only thing that can still make a
            Kategorie disappear. Leaving the sentence on the price clause would
            keep warning about an ambiguity this change removed, and stay silent
            about the one it concentrated.

            The index clause is also the only place `dateless_groups` can
            surface: a group with observations but no readable date would
            otherwise render as a tile with data and a silently missing date. */}
        {!indexLoading && !indexUnavailable && (indexIncomplete || pricesIncomplete) && (
          <div className="preisspiegel-page__truncated" role="alert">
            <strong>Achtung: unvollständige Daten.</strong>{' '}
            {indexShortfall && (
              <>
                Die Übersicht selbst konnte nicht vollständig gelesen werden (
                {indexQuality.observationCount.toLocaleString('de-DE')} Beobachtungen erfasst) — auch ganze Kategorien
                können deshalb in der Übersicht fehlen.{' '}
              </>
            )}
            {indexQuality.datelessGroups > 0 && (
              <>
                Bei einzelnen Positionen fehlt das Datum der letzten Beobachtung — der angezeigte Stand kann daher
                älter sein als die tatsächlich letzte Lieferung.{' '}
              </>
            )}
            {/* #2944 — the counterpart to the tile's bare headline. The tile
                says THAT its unit is missing; this says how many are affected
                and why, in the one place a reader looks when something on the
                page seems wrong. */}
            {indexQuality.mixedUnitItems > 0 && (
              <>
                Für {indexQuality.mixedUnitItems.toLocaleString('de-DE')}{' '}
                {indexQuality.mixedUnitItems === 1 ? 'Position' : 'Positionen'} liegen Preise in mehreren Einheiten vor
                — dort wird keine Einheit angezeigt, weil eine Überschrift nur eine tragen kann.{' '}
              </>
            )}
            {pricesIncomplete && (
              <>
                {/* #2694 — this used to fork on `visibleItems.length`, because a
                    catalog-wide read could come back truncated having dropped
                    every cell of the Kategorie on screen, leaving a grid the
                    lens still showed with nothing in it. The narrowed read
                    cannot reach that state: an empty Kategorie issues no request
                    (`enabled: items.length > 0`), so it holds no data_quality
                    and `pricesIncomplete` is false. The empty-Kategorie sentence
                    was therefore unreachable — removed rather than left to read
                    as a live case.

                    The SCOPE of the sentence follows the scope of the read. Once
                    the reader has drilled in, the request covered one item, so
                    claiming "in dieser Kategorie" would warn about siblings this
                    read never touched — the same over-claim, one level down. */}
                Es konnten nicht alle Preisbeobachtungen geladen werden (
                {priceQuality.observationCount.toLocaleString('de-DE')} gelesen). Verlauf, Spannen und Durchschnitte{' '}
                {selectedItem ? 'für diese Position' : 'in dieser Kategorie'} sind möglicherweise unvollständig.{' '}
              </>
            )}
            Bitte wenden Sie sich an den Support.
          </div>
        )}

        {/* #2420: honest empty state when NOTHING has data — replaces the old
            "Keine Warengruppe ausgewählt." blank. When there IS data, the
            resolver always yields a selected (enabled) Kategorie, so the grid /
            chart below always has a catalogKat.
            #2462: gate it on !isLoading && !error so it does NOT flash during
            the initial load (its role="status" would otherwise announce "no
            data" to screen readers mid-load) and the error banner takes
            precedence — the empty copy no longer co-renders underneath it.
            During load/error catalogKat is null, so the grid/chart branch below
            renders nothing and only the PageLoadOverlay / error banner shows.

            #2694: the load state that gates it is the INDEX's, since the index
            is now what decides whether anything has data — and `indexUnavailable`
            joins the gate, because a read that FAILED must never be spoken as
            "you have no price data". This claim is only ever made on a read that
            succeeded and genuinely returned nothing. */}
        {!indexLoading && !indexUnavailable && !pricesUnavailable && !anyData ? (
          <div className="preisspiegel-page__empty" role="status">
            <p className="preisspiegel-page__empty-title">Noch keine Preisdaten</p>
            <p className="preisspiegel-page__empty-hint">
              Sobald Lieferscheine und Gutschriften mit Preisen eingelesen sind, erscheinen hier deine Preisreihen.
            </p>
          </div>
        ) : (
          <>
            {catalogKat && !selectedItem && (
              <PriceTileGrid
                items={visibleItems}
                selectedItemId={null}
                onSelectItem={handleSelectItem}
                getSeries={getSeriesForItem}
                // HOF-270 — the materialised summary, where the item has one.
                // `getSeries` stays passed and stays load-bearing: it is the
                // AC 17 fallback, and an item whose summary is missing draws from
                // it rather than from an empty tile.
                getSummary={summaryFor}
                benchmarkSeriesIds={itemBenchmarkSeriesIds}
                // HOF-377 — preferred where it answers; the series-derived label
                // stays for the `/tiles` fallback path and says the same words.
                benchmarkLevels={benchmarkLevelsFor}
                sparklines={sparklines}
                // #2694 — the item set above came from the index; these tiles
                // are known to have data. Until the price read has ANSWERED, a
                // tile with no series is UNKNOWN, not empty.
                prices={prices}
                // #2944 — the headline's unit, from the facts rather than from
                // the catalog. The two reads meet here: `getSeries` supplies the
                // prices, this supplies what they are denominated in.
                unitsByItem={unitsByItem}
              />
            )}

            {/* #2694 — the grid renders on the index, but a drilled-in chart is
                made of nothing but prices: PriceChart's own empty state ("Keine
                Daten für diese Auswahl.") would otherwise fire on a deep link
                for the whole duration of the price read — and again, forever, if
                that read failed — contradicting the index exactly the way the
                tile skeleton exists to prevent. A wait is spelled as a wait; a
                failure is left to the error banner above rather than dressed up
                as an empty chart.

                Stage 3 could not remove this wait — a chart is made of nothing
                but prices, so there is genuinely nothing honest to draw before
                they arrive. What it removed is its SIZE: the wait is now for one
                item's cells rather than for every item of the surrounding
                Kategorie. */}
            {catalogKat && selectedItem && prices === 'loading' && (
              <div className="preisspiegel-page__chart-loading" role="status" aria-busy="true">
                Preisverlauf wird geladen …
              </div>
            )}

            {catalogKat && selectedItem && prices === 'ready' && (
              <PriceChart
                item={selectedItem}
                series={seriesForItem}
                grain={grain}
                // The DEFAULT stays out of the URL (`null` deletes the key),
                // matching how `?view=alle` is never written — a shared link
                // then carries only what the sender actually changed.
                //
                // HOF-96 §1 moved the default to `week`, and this sentinel had
                // to move WITH it. Left on `day` it would make the `1T` button
                // inert: the click deletes `?g=`, `resolveGrain` re-resolves an
                // absent param, and the answer is `week` — the reader bounced
                // straight back to the grain they were leaving, with nothing in
                // the URL to explain it. The zero value is `resolveGrain`'s, and
                // the two have to be read as one fact.
                onGrainChange={(g) => setParams({ g: g === DEFAULT_GRAIN ? null : g })}
                quality={quality}
                onQualityChange={(q) => setParams({ q })}
                view={view}
                onViewChange={(v) => setParams({ view: v })}
                priorYears={priorYears}
                onTogglePriorYear={handleTogglePriorYear}
                today={today}
                day={day}
                // #2944 — the chart labels its axis from the FACTS, and these are
                // how it learns what they say. Without them every unit on this
                // chart would come from the catalog at render time, which is the
                // substitution that made a €/kg price read as €/l.
                publishedUnits={unitsByItem[selectedItem.id]}
                publishedEstimateUnits={estimateUnitsByItem[selectedItem.id]}
                // HOF-19 — the plausibility check could not run for this read,
                // so nothing on the chart is marked because it was UNCHECKED
                // rather than because it passed. Read-wide like the two flags
                // below and for the same reason, but NOT folded into
                // `dataComplete`: no cell is missing when it fires, so the chart
                // is exactly as complete as usual and the bridges stay. What
                // changes is only what the footer may claim, and it must say
                // this — an unmarked chart is what a healthy tenant looks like,
                // so an unread flag leaves a failed check and a clean chart
                // rendering identically.
                marksUnavailable={priceQuality.marksUnavailable}
                benchmarkSeries={benchmarkForItem}
                consumerBenchmarkDegraded={priceQuality.consumerBenchmarkDegraded}
                consumerBenchmarkTruncated={priceQuality.consumerBenchmarkTruncated}
                tradeBenchmarkDegraded={priceQuality.tradeBenchmarkDegraded}
                tradeBenchmarkTruncated={priceQuality.tradeBenchmarkTruncated}
                benchmarkLevels={priceQuality.benchmarkLevels}
                // #2652 — whether the chart may bridge an off-season gap with a
                // carry-forward line. A bridge asserts that NOTHING was traded
                // across a window, and that is only observable when the whole
                // population was read; the page is the only place that knows.
                //
                // Both flags withhold it, and both are scoped to the READ: one
                // aggregate whose cap applies to the whole cell stream, with no
                // per-item flag in the response. So a fired flag means "cells
                // are missing, from somewhere in this read" — which could be
                // precisely the ones inside the gap. Reasoning past that to a
                // per-series claim would be inventing completeness the read
                // cannot substantiate; the honest scope is the scope of the
                // ignorance.
                //
                // #2694 has now walked that scope down twice — CATALOG →
                // Kategorie (stage 2) → this ITEM (stage 3) — and each step was
                // sound for the same reason: the read got narrower, so the
                // ignorance did too. It cannot be tightened again. This read
                // fetched exactly the cells behind the line on screen, so the
                // flags describe that line and nothing else; a cap that fired
                // while reading the siblings no longer reaches it.
                //
                // Since HOF-271 there is exactly one price read, so
                // `priceQuality` IS the drilled item's quality here: rendering
                // this implies `selectedItem`, which is the condition the scope
                // switched on when the request was made. Before HOF-271 this
                // said `chartQuality` and not `activeQuality` — two names for
                // the same flags under this condition — and the reason held for
                // the same reason it holds now: it is the same read the banner
                // above is gated on, so the dotted line and the warning can
                // never contradict each other.
                dataComplete={!priceQuality.truncated && !priceQuality.degraded}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
